import NumberFlow, {
  continuous,
  type NumberFlowProps,
} from "@number-flow/react";
import { useRef } from "react";

import { useLiveStat } from "@/blueprint/components/LiveStat/useLiveStat";
import VisuallyHidden from "@/blueprint/components/VisuallyHidden/VisuallyHidden";

const NUMBER_FLOW_PLUGINS: NonNullable<NumberFlowProps["plugins"]> = [
  continuous,
];

// The retired LiveCleaningTracker's live-update timing: every retarget spins
// over 350ms, so back-to-back increments read as one continuous roll.
const TIMING: EffectTiming = {
  duration: 350,
  easing: "ease-out",
};

type LiveStatProps = {
  seedValue: number;
  perSecond: number;
  /**
   * Static replacement for the ticking NUMBER only (e.g. "over 305 million"),
   * so it composes with the caller's visible unit/suffix text in the
   * accessibility tree without duplicating it.
   */
  srLabel: string;
  /** Epoch ms the seed was measured at; defaults to mount time. */
  seedTimeMs?: number;
  className?: string;
  isRevealed?: boolean;
  paused?: boolean;
};

// Style with a monospaced family (font-trimMono): PP Mori has no tabular
// figures, so proportional digits would shift the layout while rolling.
const LiveStat = ({
  seedValue,
  perSecond,
  srLabel,
  seedTimeMs,
  className,
  isRevealed = true,
  paused = false,
}: LiveStatProps) => {
  const value = useLiveStat({ seedValue, perSecond, seedTimeMs });
  const displayedValueRef = useRef(seedValue);

  if (!paused) {
    displayedValueRef.current = isRevealed ? value : seedValue;
  }

  return (
    <>
      <span aria-hidden="true">
        <NumberFlow
          value={displayedValueRef.current}
          locales="en-US"
          plugins={NUMBER_FLOW_PLUGINS}
          animated={isRevealed}
          trend={1}
          transformTiming={TIMING}
          spinTiming={TIMING}
          className={className}
        />
      </span>
      <VisuallyHidden>{srLabel}</VisuallyHidden>
    </>
  );
};

export default LiveStat;
